import { useMutation, useQuery, QueryOptions, AnyQueryKey, CachedQuery } from 'react-query';
import { createError, hideSpinner, showSpinner } from '../modules/global/actions';
import { ApiRequest } from './api';
import { useDispatch } from 'react-redux'; // Currently included because error messages still go through Saga.
import { getAccountId } from './helpers';
import { useEffect, useRef } from 'react';

interface ApiHookConfig {
	loadingIndicator?: boolean;
}

const fetchApiRequest = <T>(apiRequest: ApiRequest<T>, loadingIndicator: boolean, dispatch?: any) => {
	const result = apiRequest.execute();
	if (loadingIndicator) {
		dispatch(showSpinner());
		return result.finally(() => dispatch(hideSpinner()));
	} else {
		return result;
	}
};

const createConfigWithDefaults = <T>(config: ApiHookConfig | undefined, dispatch: any, enabled: boolean) => {
	return Object.assign(
		{
			loadingIndicator: true,
			onError: (e: any) => dispatch(createError(e)),
			cacheTime: 0, // we can explicitly cache frequently used queries but we don't need to implement cache invalidation for most of the queries
			retry: false,
			refetchOnWindowFocus: false, // remove this definition if we like to enable the feature for Mun Moi,
			enabled
		},
		config
	);
};

interface QueryKeyVariables {
	accountId?: string;
}

// UseInterval-hook by Dan Abramov from https://overreacted.io/making-setinterval-declarative-with-react-hooks/
export const useInterval = (callback: () => any, delay: number | null | undefined) => {
	const savedCallback: any = useRef();

	// Remember the latest callback.
	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	// Set up the interval.
	useEffect(() => {
		function tick() {
			savedCallback.current();
		}
		if (delay !== null) {
			let id = setInterval(tick, delay);
			return () => clearInterval(id);
		}
	}, [delay]);
};

const createQueryKey = <T>(apiRequest: ApiRequest<T>): AnyQueryKey => {
	const accountId = apiRequest.requireAuthorization ? getAccountId()! : undefined;
	const queryKeyVariables: QueryKeyVariables = { accountId };
	return [apiRequest.endpointAndQueryParameters, queryKeyVariables];
};

export const getQueryKeyVariables = (query: CachedQuery<any>) => query.queryKey[1]! as QueryKeyVariables;

export const useApiQuery = <T>(apiRequest?: ApiRequest<T>, config?: ApiHookConfig & QueryOptions<any>) => {
	const dispatch = useDispatch();
	const queryKey = apiRequest !== undefined ? createQueryKey(apiRequest) : undefined;
	const queryFn = () => fetchApiRequest(apiRequest!, configWithDefaults.loadingIndicator, dispatch);
	const configWithDefaults = createConfigWithDefaults(config, dispatch, (apiRequest !== undefined));
	return useQuery(queryKey, queryFn, configWithDefaults);
};

export const useApiMutation = <T, V>(apiRequestFactory: (key: any) => ApiRequest<T>, config?: ApiHookConfig & QueryOptions<any>) => {
	const dispatch = useDispatch();
	const mutationFn = (key: any) => fetchApiRequest(apiRequestFactory(key), configWithDefaults.loadingIndicator, dispatch);
	const configWithDefaults = createConfigWithDefaults(config, dispatch, true);
	return useMutation<T, V>(mutationFn, configWithDefaults);
};